<template>
  <div class="reveal" v-if="game">
    <header class="reveal__head">
      <h2>{{ book.ownerName }}'s book</h2>
      <span class="reveal__count">Page {{ reveal.page + 1 }} of {{ book.pages.length }}</span>
    </header>

    <p class="reveal__chain">
      Book {{ reveal.book + 1 }} of {{ game.books.length }}
    </p>

    <!--
      Wrapped so every page reserves the same vertical space. See main.scss's
      .reveal__page - a drawing is a big square and a guess is one line, and
      flipping between them moved the Back/Next buttons under your thumb.
    -->
    <div class="reveal__page">
      <PageView :page="page" show-author />
    </div>

    <div v-if="iDriveReveal" class="reveal__controls">
      <button class="secondary" :disabled="atStart" @click="prev">Back</button>
      <button class="primary" @click="next">{{ lastPage ? 'Next book' : 'Next page' }}</button>
    </div>
    <p v-else class="reveal__follow">
      {{ revealDriverName }} is turning the pages.
    </p>

    <!--
      Bug report (2026-09-11): "It would be fun if we could save the books
      afterwards." Anyone, any time in the reveal - the book on screen as one
      tall PNG, and all of them once the reveal is over.
    -->
    <div class="reveal__save">
      <button class="secondary" :disabled="Boolean(saving)" @click="saveThis">
        {{ saving === 'this' ? 'Saving…' : 'Save this book' }}
      </button>
      <button v-if="done" class="secondary" :disabled="Boolean(saving)" @click="saveAll">
        {{ saving === 'all' ? `Saving ${savedCount} of ${game.books.length}…` : 'Save all books' }}
      </button>
    </div>

    <div v-if="done" class="reveal__done">
      <p>That's everyone.</p>
      <button class="secondary" @click="$emit('leave')">Back to the menu</button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import PageView from './PageView.vue';
import { saveBook } from '@/engine/bookImage';

// The payoff. Everyone watches the same page at the same time - Matt's call,
// and the reason the position lives in shared state rather than in each
// client's head. The owner of the book on screen turns their own pages.
export default {
  name: 'RevealScreen',
  components: { PageView },
  emits: ['leave'],
  data() { return { saving: '', savedCount: 0 }; },
  computed: {
    ...mapGetters(['game', 'iDriveReveal', 'revealDriverName']),
    reveal() { return this.game.reveal || { book: 0, page: 0 }; },
    book() { return this.game.books[this.reveal.book] || { pages: [], ownerName: '' }; },
    page() { return this.book.pages[this.reveal.page] || null; },
    atStart() { return this.reveal.book === 0 && this.reveal.page === 0; },
    lastPage() { return this.reveal.page + 1 >= this.book.pages.length; },
    done() { return Boolean(this.reveal.done); },
  },
  methods: {
    next() { this.$store.dispatch('revealNext'); },
    prev() { this.$store.dispatch('revealPrev'); },
    title() { return `Telestrations, ${new Date().toLocaleDateString()}`; },
    async saveThis() {
      this.saving = 'this';
      try { await saveBook(this.book, { title: this.title() }); } finally { this.saving = ''; }
    },
    // One at a time, with a breath between: the share sheet can only show one
    // file at a time, and browsers rate-limit a burst of downloads.
    async saveAll() {
      this.saving = 'all';
      this.savedCount = 0;
      try {
        for (const book of this.game.books) {
          this.savedCount += 1;
          // eslint-disable-next-line no-await-in-loop
          await saveBook(book, { title: this.title() });
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => { window.setTimeout(r, 400); });
        }
      } finally { this.saving = ''; }
    },
  },
};
</script>
